import React, { useMemo, useState } from 'react';
import {
  Download, FileSpreadsheet, FileText, FileJson, Lock, Copy, Check,
  History, Info, RefreshCw, AlertTriangle, Landmark, X,
} from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { StatusChip } from './StatusChip';
import { EXPORT_STATUS_META } from '../lib/state';
import type { PayrollExport, ExportType, ExportFormat, ExportCategory, RunState, SubmissionDestination } from '../types';

// Catalog of exports shown per section + the OFFICIAL format each is generated in.
// Mirrors the backend export.CategoryOf/OfficialFormat; a catalog (vs deriving only
// from existing records) lets us show a row with a Generate action before first use.
interface CatalogEntry { type: ExportType; label: string; category: ExportCategory; official: ExportFormat; target?: string; }
const CATALOG: CatalogEntry[] = [
  // NOT "Bank disbursement". A tenant read that name as the file to pay with, took
  // it to the M-Pesa bulk portal and paid a whole run from it (Bavic, 2026-09-28).
  // The name has to carry the warning, because the name is what people act on —
  // the section's explanatory line underneath it did not stop them.
  { type: 'bank_review', label: 'Payment preview — not for submission', category: 'report', official: 'xlsx' },
  { type: 'bank_payment', label: 'Bank payment file', category: 'integration', official: 'csv', target: 'Bank' },
  { type: 'tra_statutory', label: 'TRA / PAYE filing', category: 'integration', official: 'csv', target: 'TRA' },
  // xlsx: the NSSF/WCF portals accept Excel schedules (verified formats, template v2026.1)
  { type: 'nssf_statutory', label: 'NSSF filing', category: 'integration', official: 'xlsx', target: 'NSSF' },
  { type: 'sdl_statutory', label: 'SDL filing', category: 'integration', official: 'csv', target: 'TRA' },
  { type: 'wcf_statutory', label: 'WCF filing', category: 'integration', official: 'xlsx', target: 'WCF' },
  { type: 'heslb_statutory', label: 'HESLB filing', category: 'integration', official: 'csv', target: 'HESLB' },
  { type: 'gl_journal', label: 'GL journal', category: 'integration', official: 'csv', target: 'TRA' },
  { type: 'payroll_summary', label: 'Payroll summary', category: 'report', official: 'xlsx' },
];

const FORMAT_ICON: Record<ExportFormat, React.ComponentType<{ className?: string }>> = {
  csv: FileSpreadsheet, xlsx: FileSpreadsheet, pdf: FileText, json: FileJson,
};
const SUCCESS = new Set(['generated', 'approved', 'submitted']);
const isSuccess = (e?: PayrollExport) => !!e && SUCCESS.has(e.status);

// The current LOGICAL export for a type: prefer the latest successful official-format
// generation (the real submission file), else the latest successful any-format, else
// the latest attempt (so a failure surfaces). Everything else is history.
function currentOf(list: PayrollExport[], official: ExportFormat): PayrollExport | undefined {
  const byVer = [...list].sort((a, b) => b.version - a.version);
  return (
    byVer.find((e) => isSuccess(e) && e.format === official) ||
    byVer.find((e) => isSuccess(e)) ||
    byVer[0]
  );
}

// Friendly, human validation summary for a failed export (esp. missing payment details).
// Non-blocking warnings on a generated file (e.g. employees excluded from a WCF
// return for a missing Contract Type). Names who, with the shared remedy.
function warningSummary(e: PayrollExport): { message: string } | null {
  const warns = (e.errors ?? []).filter((x) => x.severity === 'warning');
  if (warns.length === 0) return null;
  const names = [...new Set(warns.map((x) => x.employeeName).filter(Boolean))] as string[];
  const who = names.length > 0
    ? names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3} more` : '')
    : `${warns.length} employee${warns.length === 1 ? '' : 's'}`;
  const detail = warns.find((x) => x.message)?.message ?? 'excluded from this file.';
  return { message: `${who} — ${detail}` };
}

function failureSummary(e: PayrollExport): { message: string; missingEmployees: number } {
  // Blocking errors only — warnings are shown separately on generated files.
  const errs = (e.errors ?? []).filter((x) => x.severity !== 'warning');
  // "No payout method" applies ONLY to the bank rail (account_number). Other
  // required-field failures (e.g. a missing HESLB ID on the HESLB schedule) must
  // show their own message, not a banking message.
  const bankErrs = errs.filter((x) => x.field === 'account_number');
  const missingBank = new Set(bankErrs.map((x) => x.employeeId ?? x.message));
  if (missingBank.size > 0) {
    const n = missingBank.size;
    // Name who fails (the count alone left users unable to find the person),
    // and prefer the backend's specific remedy (e.g. "awaiting approval —
    // approve it, then regenerate") over the generic no-method message.
    const names = [...new Set(bankErrs.map((x) => x.employeeName).filter(Boolean))] as string[];
    const who = names.length > 0 ? names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3} more` : '') : `${n} employee${n === 1 ? '' : 's'}`;
    const specific = bankErrs.find((x) => x.message && !/required field/i.test(x.message))?.message;
    const remedy = specific ?? 'has no bank or mobile-money payout method. Add it, then regenerate.';
    return { message: `${who}: ${remedy}`, missingEmployees: n };
  }
  // Group the remaining errors by their real message with counts, naming the
  // employee when the server provides one ("Asha Juma: gender is missing").
  const counts = new Map<string, number>();
  for (const x of errs) {
    const key = x.employeeName ? `${x.employeeName}: ${x.message}` : x.message;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const parts = Array.from(counts.entries()).map(([m, c]) => (c > 1 ? `${m} (${c})` : m));
  return { message: parts.slice(0, 2).join('; ') || 'Generation failed', missingEmployees: 0 };
}

const short = (s?: string) => (s && s.length > 16 ? `${s.slice(0, 12)}…${s.slice(-4)}` : s ?? '');

interface ExportsTabProps {
  exports: PayrollExport[];
  runState: RunState;
  busy: boolean;
  onRequest: (type: ExportType, format: ExportFormat, force?: boolean) => void;
  onDownload: (id: string) => void;
  onPreview: (id: string) => void;
  onReviewEmployees?: () => void;
  /** Statutory-submissions pilot: per-destination availability from the server. */
  destinations?: SubmissionDestination[];
  /** hasProductStrict('statutory_submissions') — the dedicated section renders only when entitled. */
  submissionsEnabled?: boolean;
  /** Record a manual submission (opens/creates the filing for this destination). */
  onRecordSubmission?: (d: SubmissionDestination) => void;
}

export const ExportsTab: React.FC<ExportsTabProps> = ({ exports, runState, busy, onRequest, onDownload, onPreview, onReviewEmployees, destinations, submissionsEnabled, onRecordSubmission }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [details, setDetails] = useState<{ entry: CatalogEntry; current?: PayrollExport; history: PayrollExport[] } | null>(null);

  const byType = useMemo(() => {
    const m = new Map<ExportType, PayrollExport[]>();
    for (const e of exports) m.set(e.type, [...(m.get(e.type) ?? []), e]);
    return m;
  }, [exports]);

  if (runState === 'draft' || runState === 'validated') {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Lock className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="font-medium text-charcoal">Exports unlock after approval</p>
        <p className="text-sm text-muted-foreground">Statutory, bank, and GL files generate from an approved or locked run.</p>
      </div>
    );
  }

  const copy = async (checksum?: string) => {
    if (!checksum) return;
    try {
      await navigator.clipboard.writeText(checksum);
      setCopied(checksum);
      setTimeout(() => setCopied((c) => (c === checksum ? null : c)), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const rowFor = (entry: CatalogEntry, dest?: SubmissionDestination) => {
    const list = byType.get(entry.type) ?? [];
    // Bank guard: a destination row must NEVER present another bank's artifact
    // as current (a legacy NMB CSV under a "CRDB bank file" label). Records
    // stamped with a different bank fall to history; unstamped legacy records
    // (pre-bank-dimension) also fall to history when a bank is pinned.
    const eligible = dest?.bankCode
      ? list.filter((e) => (e.bankCode ?? '') === dest.bankCode)
      : list;
    const current = currentOf(eligible, entry.official);
    const history = list.filter((e) => e.id !== current?.id).sort((a, b) => b.version - a.version);
    const Icon = FORMAT_ICON[(current?.format ?? entry.official)];
    const fmt = (current?.format ?? entry.official).toUpperCase();
    const generated = isSuccess(current);
    const failed = current?.status === 'failed';
    const summary = failed && current ? failureSummary(current) : null;
    const warning = generated && current ? warningSummary(current) : null;

    return (
      <div key={entry.type} className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-charcoal">{entry.label}</span>
            {entry.category === 'integration' && entry.target && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">→ {entry.target}</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 uppercase"><Icon className="h-3.5 w-3.5" />{fmt}</span>
            {current ? <StatusChip meta={EXPORT_STATUS_META[current.status]} /> : <span className="text-muted-foreground">Not generated</span>}
            {/* OUTDATED: the run was reopened/recalculated after this file was generated —
                the file stays downloadable for audit but must never look current. */}
            {current?.invalidatedAt && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800" title={`The payroll was ${current.invalidatedReason === 'run_reopened' ? 'reopened' : 'changed'} after this file was generated. Regenerate before submitting.`}>
                <AlertTriangle className="h-3 w-3" />Outdated
              </span>
            )}
            {dest?.templateVersion && <span title="Destination template version">Template v{dest.templateVersion}</span>}
            {generated && current?.generatedAt && <span>Generated {new Date(current.generatedAt).toLocaleString()}</span>}
            {/* Checksum is an integration concern (submission verification); reports keep it in metadata only. */}
            {generated && entry.category === 'integration' && current?.checksum && (
              <button type="button" onClick={() => copy(current.checksum)} className="inline-flex items-center gap-1 font-mono hover:text-charcoal" title="Copy checksum">
                {copied === current.checksum ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                {short(current.checksum)}
              </button>
            )}
          </div>
          {summary && (
            <div className="mt-1 flex items-center gap-2 text-xs text-[#991B1B]">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span>{summary.message}</span>
              {summary.missingEmployees > 0 && onReviewEmployees && (
                <button type="button" onClick={onReviewEmployees} className="underline hover:text-charcoal">Review employee</button>
              )}
            </div>
          )}
          {warning && (
            // The file GENERATED, but some employees were left out (e.g. missing
            // Contract Type on a WCF return). Amber, not red — informational.
            <div className="mt-1 flex items-start gap-2 text-xs text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warning.message}</span>
            </div>
          )}
          {generated && entry.type === 'bank_payment' && (() => {
            // The bank file intentionally omits mobile-money employees (they're paid
            // via the disbursement connector), so it can legitimately have fewer rows
            // than the run. Spell that out so a smaller count doesn't read as missing data.
            const bankRows = Number((current?.metadata as any)?.rows ?? current?.totals?.employee_count);
            const summaryRec = currentOf(byType.get('payroll_summary') ?? [], 'csv');
            const total = Number((summaryRec?.metadata as any)?.rows ?? summaryRec?.totals?.employee_count);
            const excluded = Number.isFinite(total) && Number.isFinite(bankRows) ? total - bankRows : NaN;
            return (
              <div className="mt-1 flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {Number.isFinite(bankRows) && Number.isFinite(total) && total > 0
                    ? `${bankRows} of ${total} employees. `
                    : ''}
                  {Number.isFinite(excluded) && excluded > 0
                    ? `${excluded} mobile-money ${excluded === 1 ? 'employee is' : 'employees are'} paid through the disbursement connector, not the bank file.`
                    : 'Mobile-money employees are paid through the disbursement connector, not the bank file.'}
                </span>
              </div>
            );
          })()}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {generated && current && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onDownload(current.id)} className="border-2 border-border bg-card font-medium text-charcoal hover:border-orange-500">
              <Download className="mr-1.5 h-4 w-4" />Download {entry.category === 'integration' ? fmt : ''}
            </Button>
          )}
          {generated && current && entry.category === 'integration' && (
            <button type="button" disabled={busy} onClick={() => onPreview(current.id)} className="rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-orange-500 hover:text-charcoal disabled:opacity-50" title="Excel preview (not the official file)">
              Excel preview
            </button>
          )}
          {/* Regenerate forces a NEW immutable version; a plain Generate on an
              unchanged run reuses the existing artifact (idempotent). Submission
              destinations get a NAMED generate action ("Generate CRDB Bank File"). */}
          {dest && !current ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onRequest(entry.type, entry.official, false)} className="border-2 border-border bg-card font-medium text-charcoal hover:border-orange-500">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              {entry.type === 'bank_payment' && dest.bankCode ? `Generate ${dest.bankCode} Bank File` : `Generate ${entry.label}`}
            </Button>
          ) : (
            <button type="button" disabled={busy} onClick={() => onRequest(entry.type, entry.official, !!current)} className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-orange-500 hover:text-charcoal disabled:opacity-50" title={current ? 'Regenerate' : 'Generate'} aria-label={current ? 'Regenerate' : 'Generate'}>
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
          {dest && generated && current && onRecordSubmission && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onRecordSubmission(dest)} className="border-2 border-border bg-card font-medium text-charcoal hover:border-orange-500" title="Record that this file was manually uploaded (submission date, reference, acknowledgement)">
              Record submission
            </Button>
          )}
          {(current || history.length > 0) && (
            <button type="button" onClick={() => setDetails({ entry, current, history })} className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-orange-500 hover:text-charcoal" title="Details & history" aria-label="Details and history">
              <Info className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Statutory & Payment Submissions (pilot): bank/NSSF/WCF get their own
  // destination-aware section; the remaining integrations stay put. Without the
  // entitlement, everything renders exactly as before.
  const showSubmissions = !!submissionsEnabled && (destinations?.length ?? 0) > 0;
  const submissionTypes = new Set(showSubmissions ? ['bank_payment', 'nssf_statutory', 'wcf_statutory'] : []);
  // The generic disbursement REVIEW file (what gets sent) is split OUT of the
  // provider-specific submissions — it is reviewed on the run, the bank-specific
  // file is for filing.
  const reviewDest = (destinations ?? []).find((d) => d.exportType === 'bank_review');
  const submitDests = (destinations ?? []).filter((d) => d.exportType !== 'bank_review');
  const integrations = CATALOG.filter((c) => c.category === 'integration' && !submissionTypes.has(c.type));
  const reports = CATALOG.filter((c) => c.category === 'report');

  const submissionRow = (d: SubmissionDestination) => {
    const cat = CATALOG.find((c) => c.type === d.exportType);
    const isBank = d.exportType === 'bank_payment';
    const label = isBank
      ? d.configured && d.bankCode ? `${d.bankCode} bank file` : 'Bank payment file'
      : cat?.label ?? d.exportType;
    if (!d.configured) {
      return (
        <div key={d.exportType} className="flex flex-col gap-1 rounded-lg border border-border bg-card p-3">
          <div className="flex items-center gap-2">
            <span className="font-medium text-charcoal">{label}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">→ {d.target}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Not configured</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {isBank
              ? 'A verified payroll-upload format has not been configured for this bank.'
              : 'No verified upload format is configured for this destination.'}
          </p>
        </div>
      );
    }
    const entry: CatalogEntry = {
      type: d.exportType,
      label,
      category: cat?.category ?? 'integration',
      official: (d.defaultFormat ?? cat?.official ?? 'xlsx') as ExportFormat,
      target: d.target,
    };
    return (
      <div key={d.exportType} className="space-y-1">
        {rowFor(entry, d)}
        {isBank && d.bankCode === 'CRDB' && (
          <p className="px-1 text-xs text-muted-foreground">
            Download the CRDB-formatted payroll file and upload it manually to the CRDB portal. Electronic submission is not yet enabled.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {showSubmissions && reviewDest && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-charcoal">Payment preview</h3>
            <span className="text-xs text-muted-foreground">— a check of exactly what this run will pay (name, bank, account, amount). It is not a payment instruction: paying from it skips the checks your provider's file applies. Use the provider file below to pay.</span>
          </div>
          <div className="space-y-2">{submissionRow(reviewDest)}</div>
        </section>
      )}

      {showSubmissions && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-charcoal">Statutory &amp; Payment Submissions</h3>
            <span className="text-xs text-muted-foreground">— validated, versioned upload files; download and submit manually. SFTP/API delivery is not yet enabled.</span>
          </div>
          <div className="space-y-2">{submitDests.map(submissionRow)}</div>
        </section>
      )}

      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-charcoal">Integrations</h3>
          <span className="text-xs text-muted-foreground">— official files submitted to banks &amp; authorities (download &amp; send)</span>
        </div>
        <div className="space-y-2">{integrations.map((c) => rowFor(c))}</div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-charcoal">Reports</h3>
          <span className="text-xs text-muted-foreground">— human-readable workbooks for review</span>
        </div>
        <div className="space-y-2">{reports.map((c) => rowFor(c))}</div>
      </section>

      {details && (
        <DetailsPanel
          data={details}
          onClose={() => setDetails(null)}
          onDownload={onDownload}
          onPreview={onPreview}
          onCopy={copy}
          copied={copied}
        />
      )}
    </div>
  );
};

// --- integration details + history panel ----------------------------------
const DetailsPanel: React.FC<{
  data: { entry: CatalogEntry; current?: PayrollExport; history: PayrollExport[] };
  onClose: () => void;
  onDownload: (id: string) => void;
  onPreview: (id: string) => void;
  onCopy: (checksum?: string) => void;
  copied: string | null;
}> = ({ data, onClose, onDownload, onPreview, onCopy, copied }) => {
  const { entry, current, history } = data;
  const total = current?.totals?.net;
  // Payment count = rows actually written to this file (bank rows exclude
  // mobile-money), not the run-wide employee count.
  const count = (current?.metadata as any)?.rows ?? current?.totals?.employee_count;
  const currency = (current?.metadata as any)?.currency;
  const batchRef = (current?.metadata as any)?.batch_reference;

  const Field = ({ label, value }: { label: string; value?: React.ReactNode }) =>
    value == null || value === '' ? null : (
      <div className="flex justify-between gap-4 py-1 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-right font-medium text-charcoal">{value}</span>
      </div>
    );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-charcoal">{entry.label}</h3>
            {entry.target && <p className="text-xs text-muted-foreground">Submitted to {entry.target}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-charcoal"><X className="h-4 w-4" /></button>
        </div>

        <div className="divide-y divide-border">
          <Field label="Format" value={(current?.format ?? entry.official).toUpperCase()} />
          <Field label="Status" value={current ? <StatusChip meta={EXPORT_STATUS_META[current.status]} /> : 'Not generated'} />
          <Field label="Generated" value={current?.generatedAt ? new Date(current.generatedAt).toLocaleString() : undefined} />
          <Field label="Payments" value={count} />
          <Field label="Total" value={total ? `${currency ?? ''} ${Number(total).toLocaleString()}`.trim() : undefined} />
          <Field label="Batch reference" value={batchRef} />
          <Field label="Template version" value={current?.templateVersion} />
          <Field label="Bank" value={current?.bankCode} />
          <Field label="Outdated since" value={current?.invalidatedAt ? new Date(current.invalidatedAt).toLocaleString() : undefined} />
          <Field label="Checksum algorithm" value={current?.checksumAlgorithm} />
          {current?.checksum && (
            <div className="py-2 text-sm">
              <div className="mb-1 text-muted-foreground">Checksum</div>
              <button type="button" onClick={() => onCopy(current.checksum)} className="flex w-full items-center gap-2 break-all rounded border border-border p-2 text-left font-mono text-xs hover:border-orange-500">
                {copied === current.checksum ? <Check className="h-3.5 w-3.5 shrink-0 text-green-600" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
                {current.checksum}
              </button>
            </div>
          )}
          <Field label="Generated by" value={current?.generatedBy} />
        </div>

        {isSuccess(current) && current && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onDownload(current.id)} className="border-2 border-border font-medium text-charcoal hover:border-orange-500">
              <Download className="mr-1.5 h-4 w-4" />Download {(current.format ?? entry.official).toUpperCase()}
            </Button>
            {entry.category === 'integration' && (
              <button type="button" onClick={() => onPreview(current.id)} className="rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:border-orange-500 hover:text-charcoal">Excel preview</button>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-charcoal"><History className="h-4 w-4" />Version history</div>
            <div className="space-y-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded border border-border px-2 py-1.5 text-xs">
                  <span className="text-muted-foreground">v{h.version} · {h.format.toUpperCase()}</span>
                  <StatusChip meta={EXPORT_STATUS_META[h.status]} />
                  {isSuccess(h) ? (
                    <button type="button" onClick={() => onDownload(h.id)} className="text-muted-foreground hover:text-charcoal" title="Download this version" aria-label="Download this version"><Download className="h-3.5 w-3.5" /></button>
                  ) : <span className="w-3.5" />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
